import {
  Box,
  CircularProgress,
  FormControlLabel,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
} from "@mui/material";
import { extractErrorMessage, useInvalidateQuery } from "@platform/api";
import {
  type CorrectionOut,
  reviseReviewMutation,
  type RunDetailOut,
  type RunDiscrepancyOut,
  type RunMatchedFieldOut,
  type RunScoreOut,
  type ScoreBucketOut,
} from "@platform/api/golden";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Chip,
  ErrorBoundary,
  Link,
  MenuItem,
  Select,
  Typography,
} from "@platform/design-system";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router";

import { useGoldenClient } from "../api/GoldenClientProvider";
import { GOLDEN_TAGS } from "../api/goldenTags";
import { useCaseCorrections } from "../case/caseQueries";
import { usePageTitle } from "../hooks/usePageTitle";
import { DiscrepancyFacts } from "../review/DiscrepancyFacts";
import {
  askedForHelp,
  defaultVerdict,
  VERDICT_LABELS,
} from "../review/verdicts";
import { type Verdict, VerdictForm } from "../review/VerdictForm";
import { RfpLink } from "../shared/RfpLink";
import {
  formatTimestamp,
  formatValue,
  percent,
  toTypedText,
} from "../shared/values";

import {
  ACCURACIES,
  applyCoverageFilter,
  DISCREPANCY_KINDS,
  discrepancyKind,
  type ScoreTotals,
  totalOf,
} from "./runScore";
import { RunSteps } from "./RunSteps";
import { isRunGoing, useCaseRun } from "./runQueries";
import { Fact, factsSx, RunFacts } from "./RunSummary";
import { type RunStream, useRunStream } from "./useRunStream";

import type { ReactNode } from "react";

export const RUN_PAGE_TEST_ID = "golden-run";

const ID = /^[1-9][0-9]*$/;
// The coverage filter's "all" choice. An empty value would leave the field's label
// sitting on it.
const ALL_COVERAGES = "all";
const ALL_DISCREPANCIES = "all";

/**
 * Accuracy on the fields RUMI and the analyst both filled; the fields only one of
 * them filled are counted apart.
 */
const accuracyText = ({
  bothFilled,
  bothFilledCorrect,
}: ScoreTotals): string =>
  bothFilled === 0
    ? "no fields both filled"
    : `${percent(bothFilledCorrect / bothFilled)} (${bothFilledCorrect} of ${bothFilled} fields both filled)`;

const ScoreSummary = ({
  score,
  buckets,
}: {
  readonly score: RunScoreOut;
  readonly buckets: readonly ScoreBucketOut[];
}): ReactNode => {
  const all = totalOf(buckets);
  return (
    <Box component="dl" data-testid={`${RUN_PAGE_TEST_ID}-score`} sx={factsSx}>
      {ACCURACIES.map(({ category, label }) => (
        <Fact key={category} term={label}>
          {accuracyText(
            totalOf(buckets.filter((bucket) => bucket.category === category)),
          )}
        </Fact>
      ))}
      <Fact term="RUMI-only fields">{all.filled - all.bothFilled}</Fact>
      <Fact term="Analyst-only fields">{all.blankExpected}</Fact>
      <Fact term="Disagreed fields">{all.disagreed}</Fact>
      <Fact term="Ambiguous pairings">{all.ambiguous}</Fact>
      <Fact term="Waiting for the judge">{all.pendingJudge}</Fact>
      <Fact term="Scored">
        {formatTimestamp(score.scored_at)} against correction revision{" "}
        {score.correction_revision}
      </Fact>
    </Box>
  );
};

/** The correction a both_wrong verdict stored on a field: the one no later verdict replaced. */
const storedCorrection = (
  corrections: readonly CorrectionOut[],
  fieldPath: string,
): CorrectionOut | undefined =>
  corrections.find(
    (correction) =>
      correction.field_path === fieldPath &&
      correction.kind === "value" &&
      correction.superseded_by == null &&
      correction.revoked_revision == null,
  );

type VerdictFormForProps = {
  readonly caseId: number;
  readonly item: RunDiscrepancyOut;
  readonly pending: boolean;
  readonly error: string | undefined;
  readonly onSubmit: (verdict: Verdict) => void;
  readonly onCancel: (() => void) | undefined;
};

const verdictFormProps = (
  { item, pending, error, onSubmit, onCancel }: VerdictFormForProps,
  correctValue: string | undefined,
) => {
  const evidence = item.evidence ?? [];
  const reviewed = item.review ?? undefined;
  return {
    result: item.field_result,
    evidence,
    initial: {
      verdict: reviewed?.verdict ?? defaultVerdict(evidence),
      reason: reviewed?.reason ?? "",
      correctValue,
    },
    submitLabel: reviewed ? "Save verdict" : "Record verdict",
    pending,
    error,
    onSubmit,
    onCancel,
  };
};

// Changing a both_wrong verdict opens on the value it stored, so saving a new reason
// alone keeps the correction rather than replacing it with whatever the field held.
const BothWrongVerdictForm = (props: VerdictFormForProps): ReactNode => {
  const corrections = useCaseCorrections(props.caseId);
  // Cached corrections can predate another reviewer's change, and the form reads its
  // values once, so it waits for corrections fetched since the editor opened. Later
  // background refetches leave it, and what the reviewer typed, alone.
  const isCurrent = corrections.isSuccess && corrections.isFetchedAfterMount;
  if (!isCurrent) {
    return corrections.isError ? (
      <Alert severity="error">
        {extractErrorMessage(
          corrections.error,
          "Failed to load the stored correction",
        )}
      </Alert>
    ) : (
      <CircularProgress aria-label="Loading the stored correction" />
    );
  }
  const stored = storedCorrection(
    corrections.data,
    props.item.field_result.field_path,
  );
  return (
    <VerdictForm
      {...verdictFormProps(
        props,
        stored ? toTypedText(stored.corrected_value) : undefined,
      )}
    />
  );
};

const VerdictFormFor = (props: VerdictFormForProps): ReactNode =>
  props.item.review?.verdict === "both_wrong" ? (
    <BothWrongVerdictForm {...props} />
  ) : (
    <VerdictForm {...verdictFormProps(props, undefined)} />
  );

const DiscrepancyCard = ({
  caseId,
  item,
}: {
  readonly caseId: number;
  readonly item: RunDiscrepancyOut;
}): ReactNode => {
  const client = useGoldenClient();
  const invalidateQuery = useInvalidateQuery();
  const [editing, setEditing] = useState(false);
  const [evidenceError, setEvidenceError] = useState<string>();
  const result = item.field_result;
  const evidence = item.evidence ?? [];
  const reviewed = item.review ?? undefined;
  // A changed verdict can revoke a correction and store another, moving the case's
  // revision and the queue.
  const revise = useMutation({
    ...reviseReviewMutation({ client }),
    onSuccess: async () => {
      setEditing(false);
      await Promise.all(
        [
          GOLDEN_TAGS.runs,
          GOLDEN_TAGS.reviews,
          GOLDEN_TAGS.corrections,
          GOLDEN_TAGS.cases,
        ].map((tags) => invalidateQuery({ tags: [...tags] })),
      );
    },
  });

  const save = ({ verdict, reason, correctedValue }: Verdict): void =>
    revise.mutate({
      path: { field_result_id: result.id },
      body: { verdict, reason, corrected_value: correctedValue },
    });

  return (
    <Card data-testid={`${RUN_PAGE_TEST_ID}-discrepancy-${result.id}`}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <DiscrepancyFacts
          caseId={caseId}
          result={result}
          evidence={evidence}
          onEvidenceError={setEvidenceError}
        />
        {evidenceError && <Alert severity="error">{evidenceError}</Alert>}
        {reviewed && !editing ? (
          <Box
            data-testid={`${RUN_PAGE_TEST_ID}-verdict-${result.id}`}
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Typography variant="subtitle2" component="span">
              {VERDICT_LABELS[reviewed.verdict]}
            </Typography>
            <Typography
              variant="body-medium"
              color="textSecondary"
              component="span"
            >
              {reviewed.reviewer}, {formatTimestamp(reviewed.reviewed_at)}
              {reviewed.reason && ` · ${reviewed.reason}`}
            </Typography>
            <Button
              variant="tertiary"
              size="sm"
              data-testid={`${RUN_PAGE_TEST_ID}-change-${result.id}`}
              onClick={() => setEditing(true)}
            >
              Change verdict
            </Button>
          </Box>
        ) : (
          <VerdictFormFor
            caseId={caseId}
            item={item}
            pending={revise.isPending}
            error={
              revise.isError
                ? extractErrorMessage(revise.error, "The verdict was not saved")
                : undefined
            }
            onSubmit={save}
            onCancel={reviewed ? () => setEditing(false) : undefined}
          />
        )}
      </Box>
    </Card>
  );
};

/** The fields RUMI got right, collapsed to a count until expanded. */
const MatchedFields = ({
  matched,
}: {
  readonly matched: readonly RunMatchedFieldOut[];
}): ReactNode => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Card data-testid={`${RUN_PAGE_TEST_ID}-matched`} title="Matched fields">
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <Typography variant="body-medium" color="textSecondary">
          {matched.length} {matched.length === 1 ? "field" : "fields"} where
          RUMI matched the expected value.
        </Typography>
        {matched.length > 0 && (
          <Button
            variant="tertiary"
            size="sm"
            data-testid={`${RUN_PAGE_TEST_ID}-matched-toggle`}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Collapse all" : "Expand all"}
          </Button>
        )}
      </Box>
      {expanded && matched.length > 0 && (
        <Table size="small" aria-label="Matched fields">
          <TableHead>
            <TableRow>
              <TableCell>Field</TableCell>
              <TableCell>Expected</TableCell>
              <TableCell>RUMI</TableCell>
              <TableCell>Analyst sent</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {matched.map(({ field_result: result }) => (
              <TableRow key={result.id}>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {result.field_path}
                </TableCell>
                <TableCell>{formatValue(result.expected_value)}</TableCell>
                <TableCell>{formatValue(result.rumi_value)}</TableCell>
                <TableCell>{formatValue(result.baseline_value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
};

/** The run's discrepancies, filtered to one kind when the link names one. */
const RunDiscrepancies = ({
  caseId,
  runId,
  scored,
  coverage,
  discrepancies,
}: {
  readonly caseId: number;
  readonly runId: number;
  readonly scored: boolean;
  readonly coverage: string;
  readonly discrepancies: readonly RunDiscrepancyOut[];
}): ReactNode => {
  const [params, setParams] = useSearchParams();
  const kind = DISCREPANCY_KINDS.find(
    (option) => option.kind === params.get("discrepancies"),
  );
  const shown = kind
    ? discrepancies.filter((item) => discrepancyKind(item) === kind.kind)
    : discrepancies;
  // The fields RUMI asked a person about lead the list, as they do the review queue.
  const listed = [
    ...shown.filter((item) => askedForHelp(item.evidence ?? [])),
    ...shown.filter((item) => !askedForHelp(item.evidence ?? [])),
  ];
  const openCount = listed.filter((item) => item.review == null).length;

  const setKind = (value: string): void => {
    const next = new URLSearchParams(params);
    if (value === ALL_DISCREPANCIES) next.delete("discrepancies");
    else next.set("discrepancies", value);
    setParams(next, { replace: true });
  };

  return (
    <>
      {scored && (
        <Select
          data-testid={`${RUN_PAGE_TEST_ID}-discrepancy-kind`}
          label="Discrepancies"
          value={kind?.kind ?? ALL_DISCREPANCIES}
          sx={{ minWidth: 220, alignSelf: "flex-start" }}
          onChange={(event) => setKind(String(event.target.value))}
        >
          <MenuItem value={ALL_DISCREPANCIES}>All</MenuItem>
          {DISCREPANCY_KINDS.map((option) => (
            <MenuItem key={option.kind} value={option.kind}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      )}

      <Box
        component="section"
        data-testid={`${RUN_PAGE_TEST_ID}-discrepancies`}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <Typography variant="header-small" component="h2">
          Discrepancies
        </Typography>
        {scored && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Typography
              variant="body-medium"
              color="textSecondary"
              data-testid={`${RUN_PAGE_TEST_ID}-discrepancy-count`}
            >
              {listed.length}{" "}
              {listed.length === 1 ? "discrepancy" : "discrepancies"},{" "}
              {openCount} not reviewed.
            </Typography>
            {openCount > 0 && (
              <Link
                component={RouterLink}
                to={`/review?case=${caseId}&run=${runId}`}
              >
                Review the open ones
              </Link>
            )}
          </Box>
        )}
        {listed.map((item) => (
          <ErrorBoundary key={item.field_result.id}>
            <DiscrepancyCard caseId={caseId} item={item} />
          </ErrorBoundary>
        ))}
        {scored && listed.length === 0 && (
          <Typography variant="body-medium" color="textSecondary">
            {kind?.none ?? "No wrong, missing, or unexpected fields"}
            {coverage ? ` in ${coverage}` : ""} in this score.
          </Typography>
        )}
      </Box>
    </>
  );
};

type RunTab = "steps" | "review";

const panelSx = (shown: boolean) => ({
  display: shown ? "flex" : "none",
  flexDirection: "column",
  gap: 3,
});

const RunDetail = ({
  caseId,
  detail,
  stream,
}: {
  readonly caseId: number;
  readonly detail: RunDetailOut;
  readonly stream: RunStream;
}): ReactNode => {
  const [params, setParams] = useSearchParams();
  // Opens on what the run has to show: its steps while it works, its results once it ends.
  const [tab, setTab] = useState<RunTab>(() =>
    isRunGoing(detail) ? "steps" : "review",
  );
  const { run, score } = detail;
  const view = applyCoverageFilter(detail, {
    requested: params.get("coverage") ?? "",
    excludeNew: params.get("excludeNew") === "1",
  });
  const newCoverages = detail.new_coverages ?? [];

  const setParam = (name: string, value: string): void => {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    setParams(next, { replace: true });
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Typography variant="header-large" component="h1">
          Run #{run.id}
        </Typography>
        <Chip
          size="small"
          variant="subtle"
          leadingVisual={false}
          label={run.state}
        />
      </Box>
      <Box
        component="dl"
        data-testid={`${RUN_PAGE_TEST_ID}-facts`}
        sx={factsSx}
      >
        <Fact term="Case">
          <Link component={RouterLink} to={`/cases/${caseId}`}>
            #{caseId}
          </Link>
        </Fact>
        {run.suite_id != null && (
          <Fact term="Suite">
            <Link component={RouterLink} to={`/suites/${run.suite_id}`}>
              #{run.suite_id}
            </Link>
          </Fact>
        )}
        <Fact term="Source RFP">
          <RfpLink id={detail.source_rfp_id} url={detail.source_rfp_url} />
        </Fact>
        <RunFacts detail={detail} stream={stream} />
        {run.error_message && <Fact term="Error">{run.error_message}</Fact>}
      </Box>

      <Tabs
        data-testid={`${RUN_PAGE_TEST_ID}-tabs`}
        value={tab}
        onChange={(_, value: RunTab) => setTab(value)}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab
          label="RUMI steps"
          value="steps"
          id={`${RUN_PAGE_TEST_ID}-tab-steps`}
          aria-controls={`${RUN_PAGE_TEST_ID}-panel-steps`}
        />
        <Tab
          label="Review"
          value="review"
          id={`${RUN_PAGE_TEST_ID}-tab-review`}
          aria-controls={`${RUN_PAGE_TEST_ID}-panel-review`}
        />
      </Tabs>

      {/* Both panels stay mounted, so the steps keep following the run from the Review tab. */}
      <Box
        role="tabpanel"
        id={`${RUN_PAGE_TEST_ID}-panel-steps`}
        aria-labelledby={`${RUN_PAGE_TEST_ID}-tab-steps`}
        sx={panelSx(tab === "steps")}
      >
        <RunSteps stream={stream} />
      </Box>

      <Box
        role="tabpanel"
        id={`${RUN_PAGE_TEST_ID}-panel-review`}
        aria-labelledby={`${RUN_PAGE_TEST_ID}-tab-review`}
        sx={panelSx(tab === "review")}
      >
        {score && (view.coverages.length > 0 || newCoverages.length > 0) && (
          <Box
            sx={{
              display: "flex",
              gap: 3,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {view.coverages.length > 0 && (
              <Select
                data-testid={`${RUN_PAGE_TEST_ID}-coverage`}
                label="Coverage"
                value={view.coverage || ALL_COVERAGES}
                sx={{ minWidth: 220 }}
                onChange={(event) => {
                  const value = String(event.target.value);
                  setParam("coverage", value === ALL_COVERAGES ? "" : value);
                }}
              >
                <MenuItem value={ALL_COVERAGES}>All coverages</MenuItem>
                {view.coverages.map((coverage) => (
                  <MenuItem key={coverage} value={coverage}>
                    {coverage}
                    {view.isNew(coverage) ? " (new)" : ""}
                  </MenuItem>
                ))}
              </Select>
            )}
            {newCoverages.length > 0 && (
              <FormControlLabel
                label={`Exclude new coverages (${newCoverages.join(", ")})`}
                control={
                  <Checkbox
                    data-testid={`${RUN_PAGE_TEST_ID}-exclude-new`}
                    checked={params.get("excludeNew") === "1"}
                    onChange={(event) =>
                      setParam("excludeNew", event.target.checked ? "1" : "")
                    }
                  />
                }
              />
            )}
          </Box>
        )}

        <Card title="Score">
          {score ? (
            <ScoreSummary score={score} buckets={view.buckets} />
          ) : (
            <Typography variant="body-medium" color="textSecondary">
              Not scored yet.
            </Typography>
          )}
        </Card>

        {!isRunGoing(detail) && (
          <RunDiscrepancies
            caseId={caseId}
            runId={run.id}
            scored={score != null}
            coverage={view.coverage}
            discrepancies={view.discrepancies}
          />
        )}

        {score && <MatchedFields matched={view.matched} />}
      </Box>
    </>
  );
};

const LoadedRun = ({
  caseId,
  runId,
}: {
  readonly caseId: number;
  readonly runId: number;
}): ReactNode => {
  const detail = useCaseRun(caseId, runId);
  // The page holds the stream so both the steps and the estimated cost read it.
  const stream = useRunStream(caseId, runId, () => void detail.refetch());

  if (detail.isSuccess)
    return <RunDetail caseId={caseId} detail={detail.data} stream={stream} />;
  return detail.isError ? (
    <Alert data-testid={`${RUN_PAGE_TEST_ID}-error`} severity="error">
      {extractErrorMessage(detail.error, "Failed to load the run")}
    </Alert>
  ) : (
    <CircularProgress aria-label="Loading the run" />
  );
};

/** One run on a case: what ran, its score, and its discrepancies with their verdicts. */
export const RunPage = (): ReactNode => {
  const { caseId = "", runId = "" } = useParams();
  usePageTitle(`Case ${caseId}`, `Run ${runId}`);
  const isValid = ID.test(caseId) && ID.test(runId);

  return (
    <Box
      data-testid={RUN_PAGE_TEST_ID}
      sx={{ display: "flex", flexDirection: "column", gap: 3 }}
    >
      {isValid ? (
        // Keyed by run, so moving to another run starts its cards' state over.
        <LoadedRun
          key={`${caseId}/${runId}`}
          caseId={Number(caseId)}
          runId={Number(runId)}
        />
      ) : (
        <Alert data-testid={`${RUN_PAGE_TEST_ID}-not-found`} severity="warning">
          There is no run {runId} on case {caseId}.
        </Alert>
      )}
    </Box>
  );
};
